/**
 * AI render of the 3D view through the catalog backend — the same call the app makes.
 *
 * The contract (backend, 2026-09-09, `web-ai-render-contract.md`): an AI render is a
 * project of an AI type created with `POST /projects/` (multipart: the scene screenshot
 * plus a JSON payload), then polled until `rendering.complete`; the result lives on
 * files.icandesignapp.com and is read back as project fields. Without a catalog login the
 * server treats the browser as an unknown device: HD only, `quality: high` is stripped.
 *
 * Everything here is plain data and injectable I/O, so it is tested without a browser.
 */
import { language, t, type TranslationKey } from '../i18n/I18n';

export type RenderType = 'renderai' | 'airender';

export interface RenderRecord {
  /** The render project's id on the server. */
  id: string;
  /** The planner project the render was made from. */
  projectId: string;
  type: RenderType;
  createdAt: number;
  status: 'running' | 'done' | 'error';
  imageUrl?: string;
  thumbUrl?: string;
  /** The screenshot the server kept, for the before/after view of an older render. */
  initImageUrl?: string;
  width?: number;
  height?: number;
  prompt?: string;
  style?: string;
  error?: string;
}

export interface RenderResult {
  imageUrl: string;
  thumbUrl?: string;
  initImageUrl?: string;
  width?: number;
  height?: number;
}

export interface RenderStyle { id: string; label: string; prompt: string }

/**
 * Redesign styles, as the app's own AI gallery offers them: the same set, the same order
 * (`InteriorStyles.Table` — general popularity), the same names (the app's keys). `{room}` is
 * replaced by the project's room type. The ids are this app's own and are written into
 * gallery records, so they outlive the Unity member names their previews are staged from
 * (`tools/style-previews.json`).
 */
const STYLE_TABLE: Array<{ id: string; label: TranslationKey; prompt: string }> = [
  { id: 'boho', label: '#InteriorStyleBohoName', prompt: 'bohemian {room}, rattan, layered rugs, plants, earthy colours, relaxed eclectic decor' },
  { id: 'midcentury', label: '#InteriorStyleMidcenturyName', prompt: 'mid-century modern {room}, walnut, tapered legs, mustard and teal accents' },
  { id: 'modern', label: '#InteriorStyleContemporaryName', prompt: 'modern {room}, clean lines, neutral palette, designer furniture, warm ambient light' },
  { id: 'loft', label: '#InteriorStyleIndustrialName', prompt: 'industrial loft {room}, exposed brick, black metal, concrete, leather, Edison bulbs' },
  { id: 'coastal', label: '#InteriorStyleCoastalName', prompt: 'coastal {room}, airy whites and blues, linen, driftwood, bright seaside light' },
  { id: 'victorian', label: '#InteriorStyleVictorianName', prompt: 'victorian {room}, dark wood panelling, patterned wallpaper, velvet upholstery, brass and stained glass' },
  { id: 'scandinavian', label: '#InteriorStyleScandinavianName', prompt: 'scandinavian {room}, light wood, white walls, soft natural daylight, cozy textiles' },
  { id: 'eclectic', label: '#InteriorStyleEclecticName', prompt: 'eclectic {room}, mixed eras and patterns, bold art, collected objects, confident colour' },
  { id: 'traditional', label: '#InteriorStyleTraditionalName', prompt: 'traditional {room}, warm wood, symmetric layout, classic upholstery, layered textiles' },
  { id: 'rustic', label: '#InteriorStyleRusticName', prompt: 'rustic farmhouse {room}, reclaimed timber beams, stone, aged linen, handmade ceramics' },
  { id: 'shabbychic', label: '#InteriorStyleShabbyChicName', prompt: 'shabby chic {room}, distressed white paint, floral prints, vintage furniture, soft pastels' },
  { id: 'mediterranean', label: '#InteriorStyleMediterraneanName', prompt: 'mediterranean {room}, terracotta, whitewashed walls, arches, sunlit warmth' },
  { id: 'retro', label: '#InteriorStyleVintageName', prompt: 'retro vintage {room}, warm browns and orange, patterned wallpaper, chrome and smoked glass' },
  { id: 'asian', label: '#InteriorStyleAsianContemporaryName', prompt: 'asian {room}, dark lacquered wood, paper screens, low furniture, restrained ornament' },
  { id: 'seventys', label: '#InteriorStyleRetro1970sName', prompt: '1970s {room}, shag rug, burnt orange and avocado, wood panelling, curved modular sofa' },
  { id: 'neoclassic', label: '#InteriorStyleNeoclassicName', prompt: 'neoclassical {room}, slim mouldings, symmetric layout, pale stone and gold accents' },
  { id: 'memphis', label: '#InteriorStyleMemphisName', prompt: 'memphis design {room}, primary colours, squiggle patterns, geometric laminate shapes' },
  { id: 'japandi', label: '#InteriorStyleJapandiName', prompt: 'japandi {room}, natural oak, low furniture, muted tones, wabi-sabi simplicity' },
  { id: 'transitional', label: '#OtherStyleName', prompt: 'transitional {room}, classic shapes in a contemporary palette, calm neutrals, soft textures' },
  { id: 'wabisabi', label: '#InteriorStyleWabiSabiName', prompt: 'wabi-sabi {room}, imperfect handmade surfaces, plaster walls, raw wood, muted earth tones' },
  { id: 'tropical', label: '#InteriorStyleTropicalName', prompt: 'tropical {room}, rattan and bamboo, large green plants, palm prints, breezy light' },
  { id: 'southwestern', label: '#InteriorStyleSouthwesternName', prompt: 'southwestern {room}, adobe walls, terracotta, woven wool, desert colours, leather' },
  { id: 'craftsman', label: '#InteriorStyleCraftsmanName', prompt: 'craftsman {room}, quarter-sawn oak joinery, built-ins, art glass, earthy palette' },
  { id: 'artdeco', label: '#InteriorStyleArtDecoName', prompt: 'art deco {room}, geometric inlay, brass and marble, velvet, mirrored surfaces, bold symmetry' },
  { id: 'minimalist', label: '#InteriorStyleMinimalistName', prompt: 'minimalist {room}, uncluttered, few carefully chosen pieces, calm monochrome palette' },
  { id: 'classic', label: '#InteriorStyleContemporaryClassicalName', prompt: 'classic {room}, elegant mouldings, symmetric layout, refined fabrics, chandelier' },
  { id: 'brutalism', label: '#InteriorStyleBrutalismName', prompt: 'brutalist {room}, raw board-formed concrete, monolithic forms, sparse heavy furniture' },
  { id: 'arabic', label: '#InteriorStyleArabicName', prompt: 'arabic {room}, carved wood screens, geometric tilework, low majlis seating, brass lanterns' },
  { id: 'indian', label: '#InteriorStyleIndianName', prompt: 'indian {room}, carved teak, saturated jewel colours, block-print textiles, brass detail' },
  { id: 'african', label: '#InteriorStyleAfricanName', prompt: 'african {room}, mudcloth and woven baskets, carved wood, earth pigments, tribal pattern' },
  { id: 'greek', label: '#InteriorStyleGreekName', prompt: 'greek cycladic {room}, whitewashed plaster, arches, blue accents, woven textures' },
  { id: 'retrofuturistic', label: '#InteriorStyleRetrofuturisticName', prompt: 'retrofuturistic {room}, space-age curves, glossy plastic, chrome, saturated accent lighting' },
  { id: 'barbie', label: '#InteriorStyleBarbieName', prompt: 'barbie {room}, bubblegum pink palette, glossy surfaces, playful kitsch decor' },
  { id: 'bauhaus', label: '#InteriorStyleBauhausName', prompt: 'bauhaus {room}, primary colours, tubular steel furniture, pure geometry, functional layout' },
  { id: 'glam', label: '#InteriorStyleGlamName', prompt: 'hollywood glam {room}, velvet and satin, mirrored furniture, crystal lighting, deep jewel tones' },
  { id: 'maximalism', label: '#InteriorStyleMaximalismName', prompt: 'maximalist {room}, layered pattern and colour, gallery wall, abundant decor, rich textiles' },
  { id: 'farmhouse', label: '#InteriorStyleFarmhouseName', prompt: 'modern farmhouse {room}, white shiplap, black window frames, natural oak, simple shaker furniture' },
  { id: 'quietluxury', label: '#InteriorStyleQuietLuxuryName', prompt: 'quiet luxury {room}, understated neutral palette, fine natural materials, impeccable tailoring' },
  { id: 'organicmodern', label: '#InteriorStyleOrganicModernName', prompt: 'organic modern {room}, soft curved forms, warm neutrals, natural stone and wood, abundant greenery' },
  { id: 'provence', label: '#InteriorStyleFrenchCountryProvenceName', prompt: 'french country provence {room}, lavender and cream, weathered wood, toile fabric, soft daylight' },
  { id: 'spanishrevival', label: '#InteriorStyleSpanishRevivalName', prompt: 'spanish revival {room}, stucco walls, terracotta tile, wrought iron, carved dark wood' },
  { id: 'country', label: '#InteriorStyleCountryStyleName', prompt: 'country cottage {room}, painted wood, checked and floral fabrics, cosy layered comfort' },
  { id: 'chalet', label: '#InteriorStyleChaletName', prompt: 'alpine chalet {room}, timber walls and beams, stone fireplace, fur and wool, warm firelight' },
  { id: 'english', label: '#InteriorStyleEnglishName', prompt: 'english country {room}, chintz florals, dark green library walls, antique wood, layered rugs' },
];
/** The styles with their names in the interface language, read when shown. */
export const RENDER_STYLES: RenderStyle[] = STYLE_TABLE.map(({ id, label, prompt }) => ({ id, prompt, get label() { return t(label); } }));

/** The style's tile picture, staged from the app's own gallery (`tools/stage-style-previews.py`). */
export function stylePreview(id: string): string { return `/hp/ui/styles/${id}.jpg`; }

interface Env { VITE_AI_RENDER_API?: string; VITE_AI_RENDER_TEST?: string; DEV?: boolean }
const env: Env = ((import.meta as unknown as { env?: Env }).env) ?? {};

export const AI_RENDER_API_DEFAULT = env.VITE_AI_RENDER_API ?? 'https://icandesignapp.com';

type StorageLike = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;
const browserStorage = (): StorageLike | null => (typeof localStorage === 'undefined' ? null : localStorage);

/** The backend origin; a browser-local override lets a mock stand in for it. */
export function aiRenderApi(storage: StorageLike | null = browserStorage()): string {
  try { return storage?.getItem('hp.aiRenderApi') || AI_RENDER_API_DEFAULT; } catch { return AI_RENDER_API_DEFAULT; }
}

/** `?test=1` keeps a render out of the public feeds; on by default in development. */
export function aiRenderIsTest(): boolean {
  if (env.VITE_AI_RENDER_TEST !== undefined) return env.VITE_AI_RENDER_TEST === '1';
  return !!env.DEV;
}

/** A stable id for this browser, the way the app identifies a device (`X-Device`). */
export function deviceId(storage: StorageLike | null = browserStorage()): string {
  const key = 'hp.device';
  let id: string | null = null;
  try { id = storage?.getItem(key) ?? null; } catch { /* private mode: a per-session id */ }
  if (!id) {
    id = typeof crypto !== 'undefined' && 'randomUUID' in crypto ? crypto.randomUUID() : `web-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
    try { storage?.setItem(key, id); } catch { /* keep it for this session only */ }
  }
  return id;
}

/**
 * The app's own three sizes. The size travels as the `width`/`height` form fields, which is
 * what the backend actually reads — its `render_quality` field, described in the contract,
 * appears nowhere in its code (checked 2026-09-10). Those fields decide two things: whether
 * the finished picture is upscaled (the server upscales when the asked-for size is about
 * twice what the model produced, up to 4×), and, on the photo path, whether Nano Banana Pro
 * is asked for 1K or 2K. Anything above 1920 is cut back to 1024 for a request without an
 * active subscription, so Ultra HD without an account is worse than Full HD, not better.
 */
export type RenderQuality = 'HD' | 'FullHD' | 'UltraHD';
export const RENDER_QUALITIES: Array<{ id: RenderQuality; label: string; side: number; needsAccount: boolean }> = [
  { id: 'HD', label: 'HD', side: 1024, needsAccount: false },
  { id: 'FullHD', label: 'Full HD', side: 1920, needsAccount: false },
  { id: 'UltraHD', label: 'Ultra HD', side: 3840, needsAccount: true },
];

/** The asked-for frame at that size, keeping the shot's own proportion. */
export function renderSize(quality: RenderQuality, shot?: { width?: number; height?: number }): { width: number; height: number } {
  const side = RENDER_QUALITIES.find((q) => q.id === quality)?.side ?? 1024;
  const ratio = shot?.width && shot?.height ? shot.width / shot.height : 4 / 3;
  return ratio >= 1
    ? { width: side, height: Math.round(side / ratio) }
    : { width: Math.round(side * ratio), height: side };
}

export interface RenderRequest {
  type: RenderType;
  /** The user's own words (redesign) or an optional hint (photo). */
  prompt?: string;
  style?: string;
  roomType?: string;
  quality?: 'low' | 'medium' | 'high';
}

/** The `aiPayload` JSON for a request type (contract §4). */
export function buildPayload(request: RenderRequest): Record<string, unknown> {
  const text = request.prompt?.trim() ?? '';
  if (request.type === 'renderai') {
    return { prompt: text, negative_prompt: '', quality: request.quality ?? 'medium', autosize: 'true' };
  }
  const style = RENDER_STYLES.find((s) => s.id === request.style) ?? RENDER_STYLES[0];
  const room = request.roomType ? `${request.roomType.replace(/[_-]+/g, ' ')} interior` : 'interior';
  return { prompt: style.prompt.replace('{room}', room), negative_prompt: '', user_prompt: text, preprocessPrompt: true, googleAi: true };
}

export interface SubmitInput {
  type: RenderType;
  payload: Record<string, unknown>;
  jpeg: Blob;
  title: string;
  locale?: string;
  projectXml?: string;
  isPublic?: boolean;
  /** The asked-for result size; see RENDER_QUALITIES for what the server does with it. */
  size?: { width: number; height: number };
}

/** The multipart form the app sends (contract §2). */
export function buildForm(input: SubmitInput, FormDataImpl: typeof FormData = FormData): FormData {
  const form = new FormDataImpl();
  form.append('type', input.type);
  form.append('room', 'all');
  form.append('title', input.title || 'Home Planner Web');
  form.append('locale', input.locale ?? language());
  form.append('aiCrop', '0,0');
  form.append('aiPayload', JSON.stringify(input.payload));
  form.append('aiImageScreenShot', input.jpeg, 'scene.jpg');
  form.append('init_image', input.jpeg, 'scene.jpg');
  if (input.size) { form.append('width', String(input.size.width)); form.append('height', String(input.size.height)); }
  if (input.projectXml) form.append('project', input.projectXml);
  if (input.isPublic) form.append('public', 'true');
  return form;
}

export interface Transport {
  fetch?: typeof fetch;
  api?: string;
  device: string;
  jwt?: string;
  test?: boolean;
}

/**
 * The device travels as a form field / query parameter, not as the app's `X-Device`
 * header: the server reads all three (`req.header('X-Device') || req.body.device ||
 * req.query.device`), but its CORS preflight does not list `X-Device` among the allowed
 * headers, and a browser would drop the request before it left. `Authorization` is allowed.
 */
function headersOf(transport: Transport): Record<string, string> {
  const headers: Record<string, string> = {};
  if (transport.jwt) headers.Authorization = `Bearer ${transport.jwt}`;
  return headers;
}

function withDevice(url: string, device: string): string {
  return `${url}${url.includes('?') ? '&' : '?'}device=${encodeURIComponent(device)}`;
}

/** Create the render project; resolves to its id once the server has queued it. */
export async function submitRender(input: SubmitInput, transport: Transport): Promise<{ id: string }> {
  const doFetch = transport.fetch ?? fetch;
  const api = (transport.api ?? aiRenderApi()).replace(/\/$/, '');
  const form = buildForm(input);
  form.append('device', transport.device);
  const response = await doFetch(`${api}/projects/${transport.test ? '?test=1' : ''}`, { method: 'POST', headers: headersOf(transport), body: form });
  if (!response.ok) {
    let detail = '';
    try { detail = (await response.text()).replace(/\s+/g, ' ').trim().slice(0, 160); } catch { /* the status is the message then */ }
    throw new Error(t('#AiRenderRejected', `${response.status}${detail ? `: ${detail}` : ''}`));
  }
  const json = await response.json() as { id?: string; _id?: string };
  const id = json.id ?? json._id;
  if (!id) throw new Error(t('#AiRenderNoId'));
  return { id };
}

export interface WaitOptions extends Transport {
  intervalMs?: number;
  deadlineMs?: number;
  sleep?: (ms: number) => Promise<void>;
  onTick?: (elapsedMs: number) => void;
  now?: () => number;
}

/** The app gives a render two minutes; a browser tab can afford to wait longer for a busy provider. */
export const RENDER_DEADLINE_MS = 300_000;

interface RenderingState { complete?: boolean; running?: boolean; error?: string; errorCode?: string | number; width?: number; height?: number }

export type RenderCheck = { state: 'running' } | { state: 'done'; result: RenderResult } | { state: 'error'; error: string };

/** One look at a render on the server (contract §6): still running, finished with its images, or failed. */
export async function checkRender(id: string, options: Transport): Promise<RenderCheck> {
  const doFetch = options.fetch ?? fetch;
  const api = (options.api ?? aiRenderApi()).replace(/\/$/, '');
  const headers = headersOf(options);
  const base = `${api}/projects/${encodeURIComponent(id)}`;
  const text = async (field: string): Promise<string> => {
    const response = await doFetch(withDevice(`${base}/${field}`, options.device), { headers });
    if (!response.ok) throw new Error(t('#AiRenderStatusFailed', String(response.status)));
    return (await response.text()).trim();
  };
  let rendering: RenderingState;
  try { rendering = JSON.parse(await text('rendering') || '{}') as RenderingState; }
  catch (error) {
    if (error instanceof Error && /HTTP 404/.test(error.message)) return { state: 'error', error: t('#AiRenderNotFound') };
    throw error;
  }
  if (rendering.error) return { state: 'error', error: t('#AiRenderFailedWith', rendering.error) };
  if (!rendering.complete) return { state: 'running' };
  const imageUrl = await text('image.url');
  if (!imageUrl || imageUrl === 'undefined' || imageUrl === 'null') return { state: 'error', error: t('#AiRenderNoImage') };
  const optional = async (field: string) => { try { const v = await text(field); return v && v !== 'undefined' && v !== 'null' ? v : undefined; } catch { return undefined; } };
  return { state: 'done', result: { imageUrl, thumbUrl: await optional('image.big_thumb_url'), initImageUrl: await optional('image.init_image_url'), width: rendering.width, height: rendering.height } };
}

/** The wait ran out while the server was still rendering; the render itself may yet finish. */
export class RenderTimeout extends Error {
  constructor(readonly renderId: string, readonly waitedMs: number) {
    super(t('#AiRenderTimeout', Math.max(1, Math.round(waitedMs / 60_000))));
    this.name = 'RenderTimeout';
  }
}

/** Poll the render every few seconds, as the app does, until it finishes, fails or the deadline passes. */
export async function waitForRender(id: string, options: WaitOptions): Promise<RenderResult> {
  const sleep = options.sleep ?? ((ms) => new Promise<void>((resolve) => setTimeout(resolve, ms)));
  const now = options.now ?? (() => Date.now());
  const started = now();
  const deadline = started + (options.deadlineMs ?? RENDER_DEADLINE_MS);
  for (;;) {
    await sleep(options.intervalMs ?? 3000);
    options.onTick?.(now() - started);
    const check = await checkRender(id, options);
    if (check.state === 'error') throw new Error(check.error);
    if (check.state === 'done') return check.result;
    if (now() >= deadline) throw new RenderTimeout(id, now() - started);
  }
}

/** Delete a throwaway render project (contract §7); local records are removed separately. */
export async function deleteRender(id: string, transport: Transport): Promise<void> {
  const doFetch = transport.fetch ?? fetch;
  const api = (transport.api ?? aiRenderApi()).replace(/\/$/, '');
  await doFetch(`${api}/projects/`, { method: 'DELETE', headers: { ...headersOf(transport), 'Content-Type': 'application/json' }, body: JSON.stringify({ id, device: transport.device }) });
}
